/**
 * csvExport — the shared policy for resolving whatever a CSV export endpoint
 * returned into actual CSV bytes. METALARK-1180.
 *
 * An export answers one of two ways (METALARK-1060): a fast, small one returns
 * 200 text/csv and the body is the file; a large or slow one returns 202 with a
 * job id, and the finished file has to be polled for and then fetched from a
 * presigned URL. Every caller wants the same thing out the other end — an
 * ordinary CSV Blob — so the branch belongs in one place.
 *
 * This module holds that branch and nothing else. It has no imports beyond its
 * sibling `csvDownload`, and takes the app-specific pieces as callbacks:
 *
 *   getJob(jobId)  → the job's terminal state, as {status, downloadUrl}
 *   onBusy()       → hold whatever loading affordance the app uses
 *   onIdle()       → release it
 *
 * That is what lets uis/insights reuse it. The two UIs have different HTTP
 * clients, different stores and different job routes (insights polls a
 * launch-token-reachable route rather than the generic CRUD one), but the rules
 * that actually matter — the attempt budget, failing fast on a 4xx, preferring
 * the completion URL over the 202's — are identical and are only written here.
 *
 * Presenting the failure is deliberately left to the caller: uis/front raises a
 * modal, the Insights drilldown renders an inline message inside its own modal.
 */

import { readAsyncExportEnvelope } from './csvDownload.js';

export const EXPORT_POLL_INTERVAL_MS = 5000;
export const EXPORT_POLL_MAX_ATTEMPTS = 120;

/**
 * Poll an export job until it reaches a terminal state.
 *
 * @param {string} jobId
 * @param {(jobId: string) => Promise<{status: string, downloadUrl?: string, parameters?: any, statusReasons?: any}>} getJob
 * @param {object} [options]
 * @param {number} [options.pollInterval] - ms between attempts
 * @param {number} [options.maxAttempts]
 * @returns {Promise<{downloadUrl?: string, size?: number}>} the job's completion
 *   parameters, which carry the presigned `downloadUrl`.
 * @throws on job failure, or with `isTimeout` set when the budget runs out.
 */
export async function pollExportJob(jobId, getJob, options = {}) {
    const pollInterval = options.pollInterval ?? EXPORT_POLL_INTERVAL_MS;
    const maxAttempts = options.maxAttempts ?? EXPORT_POLL_MAX_ATTEMPTS;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        let job;
        try {
            job = await getJob(jobId);
        } catch (error) {
            // A 4xx means this job will never become readable (unknown id, no
            // access), so retrying only holds the loading state open for the
            // whole attempt budget — fail fast instead. Network blips and 5xx
            // are genuinely transient, so those keep polling.
            const status = error?.response?.status;
            if (status >= 400 && status < 500) throw error;
            console.warn('Error polling export job status:', error);
            continue;
        }

        if (job.status === 'completed' || job.status === 'complete') {
            return normalizeCompletion(job);
        }

        if (job.status === 'failed' || job.status === 'error') {
            throw new Error(job.statusReasons || 'Export job failed');
        }
    }

    const timeout = new Error('Export job did not complete in time');
    timeout.isTimeout = true;
    throw timeout;
}

/**
 * A completed job carries its `downloadUrl` either already lifted onto the
 * response (the narrow Insights job route does this) or inside `parameters`,
 * which the generic job route returns as a JSON string.
 */
function normalizeCompletion(job) {
    if (job.downloadUrl) return { downloadUrl: job.downloadUrl };

    let parameters = job.parameters;
    if (typeof parameters === 'string') {
        try {
            parameters = JSON.parse(parameters);
        } catch {
            parameters = {};
        }
    }
    return parameters || {};
}

/**
 * Resolve an export response into CSV bytes.
 *
 * @param {Blob} data - the response body, read as a Blob so the server's UTF-8
 *   BOM survives (METALARK-1183).
 * @param {object} deps
 * @param {Function} deps.getJob - see pollExportJob
 * @param {Function} [deps.onBusy] - called before polling begins
 * @param {Function} [deps.onIdle] - called once polling ends, success or not
 * @param {object} [deps.pollOptions]
 * @returns {Promise<Blob>} an ordinary CSV Blob, so the preview and download
 *   paths downstream stay identical for both response shapes.
 */
export async function resolveCsvExport(data, { getJob, onBusy, onIdle, pollOptions } = {}) {
    const envelope = await readAsyncExportEnvelope(data);
    if (!envelope) return data;

    if (onBusy) await onBusy();
    try {
        const completion = await pollExportJob(envelope.jobId, getJob, pollOptions);
        // Prefer the URL the worker minted at completion — its object is known
        // to exist and its hour starts then. The 202's own URL was signed before
        // the file existed, so it is only the fallback.
        const downloadUrl = completion.downloadUrl || envelope.downloadUrl;
        if (!downloadUrl) {
            throw new Error('Export job completed without a download URL');
        }

        const response = await fetch(downloadUrl);
        if (!response.ok) {
            throw new Error(`Export download failed with status ${response.status}`);
        }
        return await response.blob();
    } finally {
        if (onIdle) await onIdle();
    }
}
